/**
 * Runs to the organisation's observability (docs/plans/2026-10-hermes-adoption.md, item 4): the
 * destination every ended run is sent to as one trace (OpenTelemetry, or Langfuse), what words go with
 * it, and how delivery stands. Sits under Audit forwarding on the Audit page, as a list group
 * (Layout.tsx): the standing with Send a test trace at its end, the two choices, the fields, one Save.
 */

import { RUN_FORWARD_CONTENT_TEXT, RUN_FORWARD_KIND_TEXT, type RunForwardContent, type RunForwardKind, type RunForwardingPut, type RunForwardingTestResult, type RunForwardingView } from '@aioe/shared';
import { useState } from 'react';

import { ApiError, problemText, runForwardingRoutes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark, Tick } from './ui.tsx';
import type { ArtTone } from './ui/Art.tsx';
import { FormField } from './ui/FormSheet.tsx';

const STATES: Record<RunForwardingView['state'], { mark: 'connected' | 'working' | 'error' | 'idle'; label: string }> = {
  delivering: { mark: 'connected', label: 'Delivering' },
  behind: { mark: 'working', label: 'Catching up' },
  failing: { mark: 'error', label: 'Failing' },
  off: { mark: 'idle', label: 'Off' },
};

export function RunForwardingSection({ tone: _tone }: { tone: ArtTone }) {
  const view = useLoad(() => runForwardingRoutes.view().catch((e: unknown) => (e instanceof ApiError && e.status === 403 ? null : Promise.reject(e))), [], 30_000);
  const v = view.data ?? undefined;
  const [kind, setKind] = useState<RunForwardKind>('off');
  const [url, setUrl] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [secret, setSecret] = useState('');
  const [headers, setHeaders] = useState('');
  const [content, setContent] = useState<RunForwardContent>('metadata');
  const [beginning, setBeginning] = useState(false);
  const [busy, setBusy] = useState<'save' | 'test'>();
  const [error, setError] = useState<string>();
  const [tested, setTested] = useState<RunForwardingTestResult>();
  const stamp = v ? `${v.kind}/${v.updatedAt ?? ''}` : '';
  const [seen, setSeen] = useState('');
  if (v && stamp !== seen) {
    setSeen(stamp);
    setKind(v.kind);
    setUrl(v.destination?.url ?? '');
    setPublicKey(v.destination?.publicKey ?? '');
    setContent(v.content);
    setSecret('');
    setHeaders('');
    setBeginning(false);
  }
  if (view.data === null) return null;

  const stored = !!v && v.kind === kind && v.destination?.url === url.trim() && (kind !== 'langfuse' || v.destination?.publicKey === publicKey.trim());
  const keepsSecret = stored && v!.hasSecret;
  /** Headers, one a line as `Name: value`. */
  const headerMap = (): Record<string, string> | undefined => {
    const out: Record<string, string> = {};
    for (const line of headers.split('\n')) {
      const i = line.indexOf(':');
      if (i > 0) out[line.slice(0, i).trim()] = line.slice(i + 1).trim();
    }
    return Object.keys(out).length ? out : undefined;
  };
  const complete = kind === 'off' || (url.trim().length > 0 && (kind === 'otlp' || (publicKey.trim().length > 0 && (keepsSecret || secret.trim().length > 0))));
  const changed = kind === 'off' ? !!v && v.kind !== 'off' : !v || kind !== v.kind || !stored || content !== v.content || secret.length > 0 || headers.trim().length > 0;
  const body = (): RunForwardingPut => {
    const start = stored ? {} : { start: beginning ? ('beginning' as const) : ('now' as const) };
    if (kind === 'otlp') return { kind, url: url.trim(), ...(headerMap() ? { headers: headerMap() } : {}), content, ...start };
    if (kind === 'langfuse') return { kind, url: url.trim(), publicKey: publicKey.trim(), ...(secret.trim() ? { secretKey: secret.trim() } : {}), content, ...start };
    return { kind: 'off' };
  };
  const save = async () => {
    setBusy('save');
    setError(undefined);
    setTested(undefined);
    try {
      await runForwardingRoutes.set(body());
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const test = async () => {
    setBusy('test');
    setError(undefined);
    try {
      setTested(await runForwardingRoutes.test());
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const s = v ? STATES[v.state] : undefined;

  return (
    <ListGroup title="Run forwarding" description="Every run that ends, sent to your observability tools as one trace.">
      {view.error ? (
        <ListBlock>
          <ErrorNote error={view.error} />
        </ListBlock>
      ) : null}
      {v && s ? (
        <ListRow
          title={v.kind === 'off' ? 'Delivery' : RUN_FORWARD_KIND_TEXT[v.kind]}
          description={
            <>
              <span className="block">{v.summary}</span>
              {v.lastDeliveredAt ? (
                <span className="block">
                  Last delivered {timeAgo(v.lastDeliveredAt)} · {v.delivered.toLocaleString()} runs since this destination was set · delivery runs every {v.everySeconds} seconds.
                </span>
              ) : null}
              {tested ? (
                <span className="mt-1 block" role="status">
                  <StatusMark wrap state={tested.ok ? 'connected' : 'error'} label={tested.message} />
                </span>
              ) : null}
            </>
          }
          status={<StatusMark state={s.mark} label={s.label} />}
          control={
            v.kind !== 'off' ? (
              <Button size="sm" variant="secondary" isPending={busy === 'test'} isDisabled={Boolean(busy)} onPress={() => void test()}>
                Send a test trace
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {v ? <ListRow title="Send runs to" control={<ChoiceSelect label="Send runs to" className="w-64 max-w-full" value={kind} options={RUN_FORWARD_KIND_TEXT} onChange={setKind} />} /> : null}
      {v && kind !== 'off' ? <ListRow title="What goes with each run" control={<ChoiceSelect label="What goes with each run" className="w-64 max-w-full" value={content} options={RUN_FORWARD_CONTENT_TEXT} onChange={setContent} />} /> : null}
      {v && kind !== 'off' ? (
        <ListBlock className="grid gap-4">
          <FormField label={kind === 'otlp' ? 'Traces endpoint' : 'Langfuse address'} hint={kind === 'otlp' ? 'OTLP over HTTP. With no path, /v1/traces is added.' : undefined}>
            <input className={`${inputClass} mono`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder={kind === 'otlp' ? 'https://otel.example.com/v1/traces' : 'https://cloud.langfuse.com'} />
          </FormField>
          {kind === 'langfuse' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Public key">
                <input className={`${inputClass} mono`} value={publicKey} onChange={(e) => setPublicKey(e.target.value)} placeholder="pk-lf-…" />
              </FormField>
              <FormField label="Secret key" hint={keepsSecret ? 'Kept: type to replace it.' : undefined}>
                <input className={`${inputClass} mono`} type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={keepsSecret ? '••••••••' : 'sk-lf-…'} />
              </FormField>
            </div>
          )}
          {kind === 'otlp' && (
            <FormField label="Headers the endpoint needs" hint={`One a line, as Name: value.${keepsSecret ? ' Kept: type to replace them.' : ''}`}>
              <textarea className={`${inputClass} mono min-h-16`} value={headers} onChange={(e) => setHeaders(e.target.value)} placeholder={'Authorization: Bearer …\nx-honeycomb-team: …'} />
            </FormField>
          )}
          {!stored && (
            <Tick checked={beginning} onChange={setBeginning}>
              Send every run the platform still holds first, not only runs that end from now on
            </Tick>
          )}
        </ListBlock>
      ) : null}
      {v && (kind !== 'off' || v.kind !== 'off' || error) ? (
        <ListBlock className="grid gap-2">
          <ErrorNote error={error} />
          <div>
            <Button size="sm" variant="primary" isPending={busy === 'save'} isDisabled={!complete || !changed || Boolean(busy)} onPress={() => void save()}>
              {kind === 'off' ? 'Turn off' : 'Save'}
            </Button>
          </div>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
